import React, { useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Eye,
  Package,
  PenLine,
  Radio,
  ShieldCheck,
} from "lucide-react";
import type {
  ProdutorOnlineSetor,
  ResumoSetorFicha,
} from "../../../services/controleProducaoService";
import AssinaturaImagemModal, {
  type AssinaturaImagemVisualizacao,
} from "./AssinaturaImagemModal";

function formatarData(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function progressoSetor(setor: {
  itensTotal?: number;
  itensResolvidos?: number;
}): { feitos: number; total: number; pct: number } {
  const total = setor.itensTotal ?? 0;
  const feitos = setor.itensResolvidos ?? 0;
  const pct = total === 0 ? 0 : Math.round((feitos / total) * 100);
  return { feitos, total, pct };
}

function progressoSetores(setores: ResumoSetorFicha[]): {
  feitos: number;
  total: number;
  pct: number;
} {
  return progressoSetor({
    itensTotal: setores.reduce((acc, s) => acc + (s.itensTotal ?? 0), 0),
    itensResolvidos: setores.reduce(
      (acc, s) => acc + (s.itensResolvidos ?? 0),
      0
    ),
  });
}

type AssinaturaAcaoBarProps = {
  assinado: boolean;
  nome?: string | null;
  data?: string | null;
  podeAssinar: boolean;
  assinando: boolean;
  modoTeste?: boolean;
  motivos?: string[];
  onAssinar: () => void;
  labelAssinado: string;
  labelBotao: string;
  labelBotaoTeste: string;
  tituloPadrao: string;
  imagem?: string | null;
  onVerImagem?: () => void;
};

export const FichaDiariaAssinaturaLiderBar: React.FC<AssinaturaAcaoBarProps> = ({
  assinado,
  nome,
  data,
  podeAssinar,
  assinando,
  modoTeste,
  motivos,
  onAssinar,
  labelAssinado,
  labelBotao,
  labelBotaoTeste,
  tituloPadrao,
  imagem,
  onVerImagem,
}) => {
  const pronto = podeAssinar && !assinado && !assinando;
  const motivo = motivos?.[0];
  const titulo = assinado
    ? [nome, formatarData(data)].filter(Boolean).join(" · ")
    : motivo || tituloPadrao;

  if (assinado) {
    const classeAssinado =
      "inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 text-sm font-semibold text-emerald-700 sm:w-auto";
    if (imagem && onVerImagem) {
      return (
        <button
          type="button"
          onClick={onVerImagem}
          className={`${classeAssinado} touch-manipulation hover:bg-emerald-100`}
          title={titulo}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          {labelAssinado}
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      );
    }
    return (
      <span className={classeAssinado} title={titulo}>
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
        {labelAssinado}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={!pronto}
      onClick={onAssinar}
      title={titulo}
      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white touch-manipulation sm:w-auto hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-40"
    >
      {pronto ? (
        <PenLine className="h-4 w-4 shrink-0" aria-hidden />
      ) : (
        <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
      )}
      {assinando
        ? "Assinando…"
        : modoTeste
          ? labelBotaoTeste
          : labelBotao}
    </button>
  );
};

type FichaDiariaResumoGestaoProps = {
  setores: ResumoSetorFicha[];
  conferenciaNome?: string | null;
  conferenciaData?: string | null;
  conferenciaImagem?: string | null;
  producaoTotalAssinada?: boolean;
  producaoTotalNome?: string | null;
  producaoTotalData?: string | null;
  producaoTotalImagem?: string | null;
  podeAssinarProducaoTotal?: boolean;
  assinandoProducaoTotal?: boolean;
  mostrarAssinaturaProducaoTotal?: boolean;
  modoTeste?: boolean;
  modoNoite?: boolean;
  onSelectSetor: (setorId: number) => void;
  onAbrirFichaUnificada?: () => void;
  onAbrirFichaCompleta?: () => void;
  onAssinarProducaoTotal?: () => void;
};

const StatusLinha: React.FC<{
  label: string;
  Icon: typeof ClipboardCheck;
  assinado: boolean;
  nome?: string | null;
  data?: string | null;
  extra?: string;
  onClick?: () => void;
  acaoLabel?: string;
}> = ({ label, Icon, assinado, nome, data, extra, onClick, acaoLabel }) => {
  const conteudo = (
    <>
      {extra ? (
        <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
      ) : assinado ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      ) : (
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          {label}
        </p>
        {extra ? (
          <p className="text-sm font-semibold tabular-nums text-neutral-800">
            {extra}
          </p>
        ) : (
          <p
            className={`text-sm font-semibold ${
              assinado ? "text-emerald-700" : "text-amber-800"
            }`}
          >
            {assinado ? "Assinada" : "Pendente"}
          </p>
        )}
        {assinado && (nome || data) ? (
          <p className="truncate text-[11px] text-neutral-500">
            {[nome, formatarData(data)].filter(Boolean).join(" · ")}
          </p>
        ) : null}
      </div>
      {acaoLabel ? (
        <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-emerald-800">
          <Eye className="h-3.5 w-3.5" aria-hidden />
          {acaoLabel}
        </span>
      ) : null}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-start gap-2 rounded-lg text-left touch-manipulation hover:bg-neutral-50"
      >
        {conteudo}
      </button>
    );
  }
  return <div className="flex items-start gap-2">{conteudo}</div>;
};

const BarraProgressoSetorCard: React.FC<{
  feitos: number;
  total: number;
  pct: number;
  setorNome: string;
}> = ({ feitos, total, pct, setorNome }) => {
  if (total <= 0) return null;
  const completo = pct >= 100;
  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
          Progresso {pct}%
        </p>
        <p className="text-[10px] font-semibold tabular-nums text-neutral-500">
          {feitos}/{total}
        </p>
      </div>
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`Progresso de ${setorNome} ${pct}%`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${
            completo ? "bg-emerald-500" : "bg-primary"
          }`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
};

const ProdutoresOnlineSetor: React.FC<{
  produtores: ProdutorOnlineSetor[];
}> = ({ produtores }) => (
  <div className="mt-3 border-t border-neutral-100 pt-3">
    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
      Online agora
    </p>
    {produtores.length === 0 ? (
      <p className="text-xs text-neutral-400">Ninguém produzindo neste setor</p>
    ) : (
      <ul className="space-y-1.5">
        {produtores.map((produtor) => {
          const pausado = produtor.status === "PAUSED";
          return (
            <li
              key={produtor.userId}
              className="flex min-w-0 items-center gap-2"
            >
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  pausado ? "bg-amber-500" : "bg-emerald-500"
                }`}
                aria-hidden
              />
              <span className="min-w-0 truncate text-xs font-medium text-neutral-800">
                {produtor.nome}
              </span>
              <span
                className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide ${
                  pausado ? "text-amber-700" : "text-emerald-700"
                }`}
              >
                {pausado ? "Pausado" : "Online"}
              </span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const FichaDiariaResumoGestao: React.FC<FichaDiariaResumoGestaoProps> = ({
  setores,
  conferenciaNome,
  conferenciaData,
  conferenciaImagem,
  producaoTotalAssinada = false,
  producaoTotalNome,
  producaoTotalData,
  producaoTotalImagem,
  podeAssinarProducaoTotal = false,
  assinandoProducaoTotal = false,
  mostrarAssinaturaProducaoTotal = false,
  modoTeste,
  modoNoite,
  onSelectSetor,
  onAbrirFichaUnificada,
  onAbrirFichaCompleta,
  onAssinarProducaoTotal,
}) => {
  const [visualizacao, setVisualizacao] =
    useState<AssinaturaImagemVisualizacao | null>(null);
  const cardClass =
    "rounded-xl border border-primary/10 bg-white p-3 text-left shadow-sm transition-all duration-200 sm:p-4 hover:border-primary/30 hover:shadow-md";
  const progresso = useMemo(() => progressoSetores(setores), [setores]);
  const completo = progresso.pct >= 100 && progresso.total > 0;

  return (
    <section
      className="no-print mt-4 overflow-hidden rounded-xl border border-primary/15 bg-[#fff5f5] p-3 shadow-sm ring-1 ring-primary/5 sm:p-4"
      aria-label="Visão geral dos setores"
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-neutral-800">
            Visão geral dos setores
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
            {modoNoite
              ? "Na noite a conferência é única, com todos os itens. Os setores abaixo são só o status."
              : "Conferência da loja, produção e tempo de cada setor. Toque no setor para abrir a ficha."}
          </p>
        </div>
        {mostrarAssinaturaProducaoTotal || onAbrirFichaCompleta ? (
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {mostrarAssinaturaProducaoTotal ? (
              <FichaDiariaAssinaturaLiderBar
                assinado={producaoTotalAssinada}
                nome={producaoTotalNome}
                data={producaoTotalData}
                podeAssinar={podeAssinarProducaoTotal}
                assinando={assinandoProducaoTotal}
                modoTeste={modoTeste}
                onAssinar={() => onAssinarProducaoTotal?.()}
                labelAssinado="Produção total assinada"
                labelBotao="Assinar produção setores"
                labelBotaoTeste="Assinar produção setores (teste)"
                tituloPadrao="Produção total dos setores. Feita pelo supervisor de produção."
                imagem={producaoTotalImagem}
                onVerImagem={
                  producaoTotalImagem
                    ? () =>
                        setVisualizacao({
                          titulo: "Assinatura — Produção total dos setores",
                          imagem: producaoTotalImagem,
                          nome: producaoTotalNome,
                          data: producaoTotalData,
                        })
                    : undefined
                }
              />
            ) : null}
            {onAbrirFichaCompleta ? (
              <button
                type="button"
                onClick={onAbrirFichaCompleta}
                className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white touch-manipulation hover:bg-primary-dark"
              >
                <Radio className="h-4 w-4 shrink-0" aria-hidden />
                Ficha completa ao vivo
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {progresso.total > 0 ? (
        <div className="mb-3 rounded-xl border border-primary/10 bg-white px-3 py-2.5 sm:px-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
              Progresso geral {progresso.pct}%
            </p>
            <p className="text-[11px] font-semibold tabular-nums text-neutral-500">
              {progresso.feitos}/{progresso.total}
            </p>
          </div>
          <div
            className="mt-2 h-2.5 overflow-hidden rounded-full bg-neutral-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progresso.pct}
            aria-label={`Progresso geral ${progresso.pct}%`}
          >
            <div
              className={`h-full rounded-full transition-[width] duration-300 ${
                completo ? "bg-emerald-500" : "bg-primary"
              }`}
              style={{
                width: `${Math.min(100, Math.max(0, progresso.pct))}%`,
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {setores.map((setor) => {
          const progressoSetorAtual = progressoSetor(setor);
          const nomeConferencia = setor.conferenciaNome ?? conferenciaNome;
          const dataConferencia = setor.conferenciaData ?? conferenciaData;
          const imagemConferenciaSetor = conferenciaImagem ?? null;
          const imagemProducaoSetor = setor.producaoImagem ?? null;
          const abrirSetor = () => onSelectSetor(setor.setorId);
          const cabecalho = (
            <>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
                {setor.setorNome}
              </p>
              <BarraProgressoSetorCard
                feitos={progressoSetorAtual.feitos}
                total={progressoSetorAtual.total}
                pct={progressoSetorAtual.pct}
                setorNome={setor.setorNome}
              />
            </>
          );
          const linhaConferencia = (
            <StatusLinha
              label="Assinatura conferência"
              Icon={ClipboardCheck}
              assinado={setor.conferenciaAssinada}
              nome={nomeConferencia}
              data={dataConferencia}
              onClick={
                setor.conferenciaAssinada && imagemConferenciaSetor
                  ? () =>
                      setVisualizacao({
                        titulo: "Assinatura — Conferência",
                        imagem: imagemConferenciaSetor,
                        nome: nomeConferencia,
                        data: dataConferencia,
                      })
                  : !modoNoite
                    ? abrirSetor
                    : undefined
              }
              acaoLabel={
                setor.conferenciaAssinada && imagemConferenciaSetor
                  ? "Ver"
                  : undefined
              }
            />
          );
          const linhaProducao = (
            <StatusLinha
              label="Assinatura produção"
              Icon={Package}
              assinado={setor.producaoAssinada}
              nome={setor.producaoNome}
              data={setor.producaoData}
              onClick={
                setor.producaoAssinada && imagemProducaoSetor
                  ? () =>
                      setVisualizacao({
                        titulo: `Assinatura — Produção ${setor.setorNome}`,
                        imagem: imagemProducaoSetor,
                        nome: setor.producaoNome,
                        data: setor.producaoData,
                      })
                  : !modoNoite
                    ? abrirSetor
                    : undefined
              }
              acaoLabel={
                setor.producaoAssinada && imagemProducaoSetor
                  ? "Ver"
                  : undefined
              }
            />
          );
          const linhaTempo = (
            <StatusLinha
              label="Tempo total de produção"
              Icon={Clock3}
              assinado={setor.tempoTotalMs > 0}
              extra={setor.tempoFormatado}
              onClick={!modoNoite ? abrirSetor : undefined}
            />
          );
          const produtores = (
            <ProdutoresOnlineSetor
              produtores={setor.produtoresOnline ?? []}
            />
          );
          return (
            <article key={setor.setorId} className={cardClass}>
              {modoNoite ? (
                cabecalho
              ) : (
                <button
                  type="button"
                  onClick={abrirSetor}
                  className="block w-full text-left touch-manipulation rounded-lg transition-all duration-200 hover:opacity-90"
                >
                  {cabecalho}
                </button>
              )}
              <div className="mt-3 space-y-3">
                {linhaConferencia}
                {linhaProducao}
                {linhaTempo}
              </div>
              {produtores}
            </article>
          );
        })}
      </div>

      {modoNoite && onAbrirFichaUnificada ? (
        <button
          type="button"
          onClick={onAbrirFichaUnificada}
          className="mt-4 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-white px-4 text-sm font-semibold text-primary ring-1 ring-primary/20 touch-manipulation hover:bg-primary hover:text-white"
        >
          Abrir ficha unificada da noite
        </button>
      ) : null}

      <AssinaturaImagemModal
        visualizacao={visualizacao}
        onClose={() => setVisualizacao(null)}
      />
    </section>
  );
};

export default FichaDiariaResumoGestao;
